import { isMobile } from "../StaticConfig";
import { IWebClientMetricsLogger } from "./IWebClientMetricsLogger";
import { LocalWebClientMetricsLogger } from "./LocalWebClientMetricsLogger";
import {
  MetricsEvent,
  MetricsEventParameters,
  MetricsEventType,
} from "./MetricsEvent";
import { MetricsSuites } from "./MetricsSuites";
import { browserSupportPerfApi } from "./PerformanceMarker";

//// ============================================================================================================
//// Variables and functions to handle MetricsContext
//// ============================================================================================================
// Global variable to store context id, which is the identifier for each element in contextStack
let nextContextId = 0;
// Global stack to store all MetricsContext created by MetricsController.CreateContext
const contextStack: Array<MetricsContext> = [];
// Local logger which logs metrics event to browser's devtool
const localEventLogger: IWebClientMetricsLogger =
  new LocalWebClientMetricsLogger();
// Boolean variable to check if a local logging is supported.
const reportLocalMetrics = browserSupportPerfApi();
// Create a MetricsContext object
export function createContext(
  suite: MetricsSuites,
  description: string,
  extraInfo?: string,
  isBreakdownContext = false,
  setStartToTimeZero = false
): MetricsContext {
  const newContext = new MetricsContext(
    getContextIdThenIncrement(),
    suite,
    description,
    extraInfo,
    isBreakdownContext,
    setStartToTimeZero
  );
  isBreakdownContext ? null : contextStack.push(newContext);
  return newContext;
}
// Close the provided MetricsContext object and stop the timing.  Removes any orphaned nested contexts in the
// process.
function closeContext(context: MetricsContext): void {
  const id = context.id;
  let pos = -1;

  for (let i = contextStack.length - 1; i >= 0; i--) {
    if (contextStack[i].id === id) {
      pos = i;
      break;
    }
  }

  if (pos !== -1) {
    const cnt = contextStack.length - pos;
    for (let i = 0; i < cnt; i++) {
      contextStack.pop();
    }
  }

  logContextEnd(context);
}
// Log the timing context end by creating and logging a MetricsEvent out of it. This is when the elapsed time is
// taken for the context
export function logContextEnd(context: MetricsContext): void {
  const parameters = buildMetricsEventCommonParameters(context);
  parameters.time = context.endTime();
  parameters.elapsed = context.elapsedMS();
  parameters.isMobile = isMobile(document) || false;

  const metricsEvent = new MetricsEvent(
    MetricsEventType.contextEnd,
    context.metricSuite,
    parameters
  );

  logMetricsEvent(metricsEvent);
}
// Capture the timing context end without logging a MetricsEvent. This function is used to capture telemetry breakdown
// information
export function captureContextEnd(context: MetricsContext): void {
  const parameters = buildMetricsEventCommonParameters(context);
  parameters.time = context.endTime();
  parameters.elapsed = context.elapsedMS();
  parameters.isMobile = isMobile(document) || false;
}
// Logs the given event to the chrome dev tools and any reporters wired up.
export function logMetricsEvent(evt: MetricsEvent): void {
  if (reportLocalMetrics && evt.eventType !== MetricsEventType.sessionInit) {
    localEventLogger.logEvent(evt);
  }
  // if this metrics suite is currently filtered out, simply ignore
  if (
    evt.metricSuite === MetricsSuites.None ||
    (evt.metricSuite & metricsSuitesFilter) !== evt.metricSuite
  ) {
    return;
  }

  if (eventLoggers.length > 0) {
    sendMetricEventToAllLoggers(evt);
  } else {
    metricsEventBuffer.eventBuffer.push(evt);
  }
}
// Get current contextId, then increment it by one. This function is used in MetricsController
export function getContextIdThenIncrement(): number {
  const id = nextContextId;
  ++nextContextId;
  return id;
}
// Build common parameters for MertricsEvent
function buildMetricsEventCommonParameters(
  context: MetricsContext
): MetricsEventParameters {
  const parameters = new MetricsEventParameters();
  parameters.id = context.id.toString();
  parameters.description = context.description;
  if (context.extraInfo !== null && context.extraInfo !== undefined) {
    parameters.extraInfo = context.extraInfo;
  }
  if (context.clientEventId !== null && context.clientEventId !== undefined) {
    parameters.clientEventId = context.clientEventId;
  }

  return parameters;
}

//// ============================================================================================================
//// Variables and functions to handle MetricsSuites filter and session type
//// ============================================================================================================
// MetricsSuites name lookup table used for parsing
const SuiteNameLookup: Record<string, MetricsSuites> = {
  none: MetricsSuites.None,
  navigation: MetricsSuites.Navigation,
  bootstrap: MetricsSuites.Bootstrap,
  rendering: MetricsSuites.Rendering,
  commands: MetricsSuites.Commands,
  toolbar: MetricsSuites.Toolbar,
  hittest: MetricsSuites.HitTest,
  debug: MetricsSuites.Debug,
  fonts: MetricsSuites.Fonts,
  maps: MetricsSuites.Maps,
  exporting: MetricsSuites.Exporting,
  min: MetricsSuites.Min,
  core: MetricsSuites.Core,
  all: MetricsSuites.All,
};
// Global variable for MetricsSuites settings. MetricsSuites value serves as flags to control what telemetry events
// to log. For example, a typical Tableau Cloud settings for MetricsSuites is MetricsSuites.core
let metricsSuitesFilter = MetricsSuites.None;
// Use the value of TsConfig.metricsFilter to set metricsSuitesFilter value
export function setMetricsSuitesFilter(metricsFilter: string): void {
  if (
    metricsFilter !== undefined &&
    metricsFilter !== null &&
    metricsFilter !== ""
  ) {
    // iterate through possibly '|'-delimited list of suites to enable
    const suites = metricsFilter.split("|");
    for (const suite of suites) {
      const trimmedSuite = suite.trim().toLowerCase();
      // This is a safer way to check key than doing SuiteNameLookup.hasOwnProperty(trimmedSuite)
      if (Object.prototype.hasOwnProperty.call(SuiteNameLookup, trimmedSuite)) {
        metricsSuitesFilter |= SuiteNameLookup[trimmedSuite];
      }
    }
  }
}
// Get MetricsSuites filter settings. MetricsController calls this function to get metricsFilters value
export function getMetricsSuitesFilter(): MetricsSuites {
  return metricsSuitesFilter;
}
// Use the value of TsConfig.vizqlSessionType to set the current session type
let sessionType = "";
export function setSessionType(vizqlSessionType: string): void {
  sessionType = vizqlSessionType;
}

//// ============================================================================================================
//// Variables and functions to handle MetricsEvent
//// ============================================================================================================
class MetricsEventBuffer {
  // Global array to hold all metrics events. These events will be sent to all IWebClientMetricsLogger
  private readonly _eventBuffer: Array<MetricsEvent>;

  constructor() {
    this._eventBuffer = [];
  }

  get eventBuffer() {
    return this._eventBuffer;
  }

  get length() {
    return this._eventBuffer.length;
  }

  public reset(): void {
    this._eventBuffer.length = 0;
  }
}

// Global array to hold all metrics loggers
let eventLoggers: Array<IWebClientMetricsLogger> =
  new Array<IWebClientMetricsLogger>();

// Send MetricsEvent to all loggers specified in setMetricsEventLoggers
function sendMetricEventToAllLoggers(evt: MetricsEvent): void {
  evt.parameters.sessionType = sessionType;
  eventLoggers.forEach((evtLogger) => {
    evtLogger.logEvent(evt);
  });
}

const metricsEventBuffer = new MetricsEventBuffer();

// Set event logger. This function is called when vizclient application starts up
export function setMetricsEventLoggers(
  logger: Array<IWebClientMetricsLogger>
): void {
  eventLoggers = logger;
  // If a valid logger was passed in and we have buffered events, log them and clear the buffer
  if (
    eventLoggers !== null &&
    eventLoggers !== undefined &&
    metricsEventBuffer.length > 0
  ) {
    metricsEventBuffer.eventBuffer.forEach((evt) => {
      sendMetricEventToAllLoggers(evt);
    });
    metricsEventBuffer.reset();
  }
}

//// ============================================================================================================
//// The section of classes
//// ============================================================================================================
// This is the timing function migrated from MetricsController. This function is used by MetricsContext to measure
// the elapsed time
function getTiming(): () => number {
  let epoch = 0;
  if (
    typeof window !== "undefined" &&
    window.self &&
    window.self.performance &&
    typeof window.self.performance.now === "function"
  ) {
    /// window.performance.now returns time in ms since navigation start.  Use "responseStart" instead
    /// to ignore previous page's unloadEvent handling and any redirects, and to try to align the 'zero'
    /// point of these two cases as closely as possible
    const navigationEntries = window.self.performance.getEntriesByType(
      "navigation"
    ) as PerformanceNavigationTiming[];
    if (navigationEntries.length > 0) {
      epoch = navigationEntries[0].responseStart;
    } else {
      epoch = 0;
    }
    return function (): number {
      return window.self.performance.now() - epoch;
    };
  } else {
    // because window.performance.start is relative to start of page navigation, do something
    // similar, but we need to track our own Epoch
    const pageNavigationEpoch = Date.now();
    return function (): number {
      return Date.now() - pageNavigationEpoch;
    };
  }
}
// Class representing a single timing context
export class MetricsContext {
  // =========================================================================================================
  // Member Variables
  // =========================================================================================================

  // This is the context Id which is an incremental integer
  public readonly id: number;
  public readonly metricSuite: MetricsSuites;
  public readonly description: string;
  public extraInfo: string | undefined;
  protected start: number;
  public end: number;
  public clientEventId: string;
  public open: boolean;
  protected isBreakdownContext: boolean;
  private getTiming: () => number;

  private readonly localReporter: LocalMetricsContext | undefined = undefined;

  //// =========================================================================================================
  //// Constructors
  //// =========================================================================================================
  constructor(
    contextId: number,
    suite: MetricsSuites,
    desc: string,
    extraInfo?: string,
    isBreakdownContext = false,
    setStartToTimeZero = false
  ) {
    this.id = contextId;
    this.metricSuite = suite;
    this.description = desc;
    this.getTiming = getTiming();
    if (setStartToTimeZero) {
      this.start = 0;
    } else {
      this.start = this.getTiming();
    }

    this.open = true;

    this.clientEventId = "";
    this.end = -1;
    this.extraInfo = extraInfo;
    this.isBreakdownContext = isBreakdownContext;

    if (
      // When it's EPT breakdown, always log them to local metrics. Or, when extraInfo has value (implies either
      // viz load related metrics, or command metrics), also log them to local metrics
      reportLocalMetrics &&
      (isBreakdownContext || (extraInfo !== null && extraInfo !== undefined))
    ) {
      this.localReporter = new LocalMetricsContext(suite, desc, extraInfo);
    }
  }

  //// ===========================================================================================================
  //// Methods
  //// ===========================================================================================================

  // Closes the current context and stops any existing timing.
  public dispose(): void {
    this.close();
  }

  // Closes the current context and stops any existing timing.
  public close(): void {
    if (this.open) {
      this.end = this.getTiming();
      this.isBreakdownContext ? null : closeContext(this);
      this.open = false;

      if (this.localReporter !== undefined && this.localReporter !== null) {
        this.localReporter.close();
      }
    }
  }

  // Returns the number of milliseconds elapsed since this context was opened. If the context has been closed,
  // returns the milliseconds that elapsed between creation and close of the context
  public elapsedMS(): number {
    if (!this.open) {
      return this.end - this.start;
    }
    return this.getTiming() - this.start;
  }

  // Gets the End date/time of this context (if it's been closed)
  // This function currently doesn't have a mapping in C# layer because it's not used. We should add one if C#
  // code needs to call this function
  public endTime(): number {
    return this.end;
  }
}

// Class representing a single timing context for the MetricsController
export class LocalMetricsContext extends MetricsContext {
  //// ===========================================================================================================
  //// Member Variables
  //// ===========================================================================================================

  protected readonly metricName: string;
  protected static eventCount = 0;

  //// ===========================================================================================================
  //// Constructors
  //// ===========================================================================================================

  public constructor(suite: MetricsSuites, desc: string, extraInfo?: string) {
    super(getContextIdThenIncrement(), suite, desc);
    const descriptionName = LocalWebClientMetricsLogger.buildDescriptionName(
      desc,
      extraInfo
    );
    if (descriptionName !== undefined) {
      desc = descriptionName;
    }

    this.open = true;
    this.metricName =
      desc + "#" + (LocalMetricsContext.eventCount++).toString();
    LocalWebClientMetricsLogger.logLocalMetricStart(this.metricName);
  }

  //// ===========================================================================================================
  //// Methods
  //// ===========================================================================================================

  // Closes the current context and stops any existing timing.
  public close(): void {
    if (this.open) {
      this.open = false;
      LocalWebClientMetricsLogger.logLocalMetricEnd(this.metricName, null);
    }
  }
}
